import React, { FC, SyntheticEvent, useMemo, useState, useEffect } from 'react';
import { TableSelect } from '../../../../../../../../../../shared/components/table/styles/styleFields.ts';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { ISelectOption } from '../../../../../../../../../invoice/create/states/customerList.state.ts';
import { DictCustomersRes } from '../../../../../../../../../../shared/services/configsData/configsData.service.ts';
import { clearRowFromVirtualKeys } from '../additionalFunctions.ts';
import { defaultOrderState } from '../../../../../defaultOrder.state.ts';
import { isEnterKey } from '../../../../../../../../../../shared/helpers/utils/utils.helper.ts';
import { GridCellEditStartReasons } from '@mui/x-data-grid/models/params/gridEditCellParams';

export const CustomerEditCell: FC<ICustomerEditCell> = ({
  params,
  customerList,
  editCellReason,
}) => {
  const { api, id, field, row } = params;
  const filteredCustomerList = useMemo(() => {
    return customerList.reduce((acc: IFullOptions[], item) => {
      if (
        item?.id === row.customerId ||
        !api.getSortedRows().some((el) => el.customerId === item.id)
      ) {
        const { internalOrFullName, ...rest } = item;
        acc.push({ label: internalOrFullName, ...rest } as IFullOptions);
      }
      return acc;
    }, []);
  }, [customerList, row]);
  const { setEditCellValue, stopCellEditMode } = api;
  const defaultValue = useMemo(() => {
    return filteredCustomerList?.find((el) => el?.id === row.customerId);
  }, []);
  const [inputValue, setInputValue] = useState(
    (filteredCustomerList.find((el) => el?.customerNo === row.customerNr) &&
      row.internalOrFullName) ||
      '',
  );

  useEffect(() => {
    setEditCellValue({ id, field, value: params.formattedValue || defaultValue?.label || '' });
  }, []);

  const handleChange = (_: SyntheticEvent, v: ISelectOption | null) => {
    setEditCellValue({ id, field, value: v?.label });
    const { label, customerNo: customerNr, id: customerId } = v as IFullOptions;
    defaultOrderState.pub.updateCell(
      {
        ...clearRowFromVirtualKeys(row),
        customerNr,
        customerId,
        [field]: label,
      },
      'nameCell',
    );
    stopCellEditMode({ id, field });
  };

  /* The filter function filters options by the input text and sorts them so that matches on customerNo come first
  (with priority if they start with the input), followed by matches on label;
  the logic is intentionally replicated from Windows WaWi to make the transition easier for users. */
  const filterOptions = (
    options: ExtendedSelectOption[],
    { inputValue }: { inputValue: string },
  ) => {
    const inputValueLower = (inputValue ?? '').toLowerCase();

    const getBlock = (o: ExtendedSelectOption, q: string) => {
      const article = (o.customerNo ?? '').toLowerCase();
      const label = (o.label ?? '').toLowerCase();

      if (article.includes(q)) return 0;
      if (label.includes(q)) return 1;
      if (`${article} ${label}`.includes(q)) return 0;
      return 1;
    };

    return options
      .filter((o) => {
        const article = (o.customerNo ?? '').toLowerCase();
        const label = (o.label ?? '').toLowerCase();
        return (
          article.includes(inputValueLower) ||
          label.includes(inputValueLower) ||
          `${article} ${label}`.includes(inputValueLower)
        );
      })
      .sort((a, b) => {
        const blockA = getBlock(a, inputValueLower);
        const blockB = getBlock(b, inputValueLower);

        if (blockA !== blockB) return blockA - blockB;

        if (blockA === 0) {
          const A = (a.customerNo ?? '').toLowerCase();
          const B = (b.customerNo ?? '').toLowerCase();

          const aStarts = A.startsWith(inputValueLower);
          const bStarts = B.startsWith(inputValueLower);

          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          return (a.customerNo ?? '').localeCompare(b.customerNo ?? '', undefined, {
            numeric: false,
          });
        } else {
          return (a.label ?? '').localeCompare(b.label ?? '', undefined, {
            numeric: true,
          });
        }
      });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.code === 'Escape') {
      stopCellEditMode({ id, field });
    } else if (e.target instanceof HTMLInputElement && isEnterKey(e)) {
      stopCellEditMode({ id, field });
    }
  };
  return (
    <TableSelect
      autoFocus
      datasetattribute='customerOptions'
      openOnFocus
      size='small'
      width='100%'
      fontSize='14px'
      disableClearable
      options={filteredCustomerList as []}
      autoHighlight
      filterOptions={filterOptions}
      /* filterSelectedOptions prop enables filtering of the currently selected option only when typing from keyboard (`printableKeyDown`).
      This prevents unwanted scrolling and avoids auto-highlighting the first item in the list. */
      filterSelectedOptions={editCellReason === 'printableKeyDown'}
      defaultValue={defaultValue as ISelectOption}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input') setInputValue(newInputValue);
      }}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
    />
  );
};

interface ICustomerEditCell {
  params: GridRenderCellParams;
  customerList: Record<string, any>[];
  editCellReason: GridCellEditStartReasons | undefined;
}

interface ExtendedSelectOption extends ISelectOption {
  customerNo?: string;
}
interface IFullOptions extends Omit<DictCustomersRes[number], 'internalOrFullName'> {
  label: DictCustomersRes[number]['internalOrFullName'];
}
